import React from 'react'
import clsx from 'clsx'
import dayjs from '../utils/dayjs'
import {Typography} from '../components/Typography'
import {Patreon} from '../components/Patreon'
import {ButtonDaysCarousel} from '../components/ButtonDaysCarousel'
import {useFooterContext} from '../components/Footer'
import {Icon, Button} from '../components/Button'
import {ImgFilters} from '../components/ImgFilters'
import imageEad from '../img/ead-03.png'
import waveRight from '../img/icons/wave-right.svg'
import professores from '../img/icons/professores.svg'
import filtro from '../img/icons/filtro.svg'
import certificado from '../img/icons/certificado.svg'
import {useProfessorsQuery} from '../utils/hooks/useProfessorsQuery'

const Hero = ({imgDesktop, imgMobile, subtitle, title, button}) => {
  return (
    <div className="relative w-full h-98">
      <ImgFilters img={imgMobile} transparency paralax className="lg:hidden" />
      <ImgFilters
        img={imgDesktop}
        transparency
        paralax
        className="hidden lg:block"
      />
      <div className="absolute inset-0 px-10 lg:px-36">
        <div className="grid gap-2.5 grid-cols-4 grid-flow-col mt-28 lg:grid-cols-12 lg:gap-4">
          <div className="col-start-1 col-end-5 lg:col-start-1 lg:col-end-11">
            <Typography
              variant="heading-xl"
              weight="default"
              className="text-white lg:text-2xl text-shadow"
            >
              {subtitle}
            </Typography>
          </div>
          <div className="col-start-1 col-end-5 lg:col-start-1 lg:col-end-11">
            <Typography
              variant="heading-xl"
              className="text-white lg:text-4xl text-shadow"
            >
              {title}
            </Typography>
          </div>
          <Button
            as="a"
            href={button.link}
            className="mt-4 uppercase lg:col-start-1 lg:col-end-5 col-start-1 col-end-5"
          >
            {button.label}
          </Button>
        </div>
      </div>
    </div>
  )
}

const Chamada = ({eventInfo}) => {
  return (
    <div className="px-10 pb-16 mx-auto max-w-screen-lg lg:px-auto">
      <section className="mt-20">
        <div className="relative bg-white z-2">
          <div className="flex items-center justify-between pt-3 pb-5">
            <div
              className="w-6 lg:w-full"
              style={{height: '4px', backgroundColor: '#bdbdbd'}}
            ></div>
            <Typography
              as="h3"
              variant="heading2"
              size="lg"
              className="px-2 font-black text-center uppercase lg:whitespace-nowrap"
            >
              {eventInfo}
            </Typography>
            <div
              className="w-6 lg:w-full"
              style={{height: '4px', backgroundColor: '#bdbdbd'}}
            ></div>
          </div>
          <div className="relative text-center pb-5 grid gap-2.5 grid-cols-4 grid-flow-col lg:grid-cols-12 lg:gap-4">
            <div className="flex justify-around lg:col-start-1 lg:col-end-1 col-start-1 col-end-5">
              <Icon icon={professores} />
            </div>
            <div className="flex justify-around lg:col-start-2 lg:col-end-5 col-start-1 col-end-5">
              <div>
                <Typography as="p" align="text-center lg:text-left">
                  <Typography
                    as="strong"
                    weight="black"
                    uppercase
                    className="text-blue-500"
                  >
                    PROFESSORES RENOMADOS
                  </Typography>{' '}
                  que são{' '}
                  <Typography
                    as="strong"
                    weight="black"
                    uppercase
                    className="text-blue-500"
                  >
                    referência
                  </Typography>{' '}
                  em suas áreas de atuação.
                </Typography>
              </div>
            </div>
            <div className="flex justify-around lg:col-start-5 lg:col-end-5 col-start-1 col-end-5">
              <Icon icon={filtro} />
            </div>
            <div className="flex justify-around lg:col-start-6 lg:col-end-9 col-start-1 col-end-5">
              <div>
                <Typography as="p" align="text-center lg:text-left">
                  Conteúdo baseado nas{' '}
                  <Typography
                    as="strong"
                    weight="black"
                    uppercase
                    className="text-blue-500"
                  >
                    PRINCIPAIS EVIDÊNCIAS CIENTÍFICAS
                  </Typography>{' '}
                  da atualidade.
                </Typography>
                <p></p>
              </div>
            </div>
            <div className="flex justify-around lg:col-start-9 lg:col-end-9 col-start-1 col-end-5">
              <Icon icon={certificado} />
            </div>
            <div className="flex justify-around lg:col-start-10 lg:col-end-13 col-start-1 col-end-5">
              <div>
                <Typography as="p" align="text-center lg:text-left">
                  <Typography
                    as="strong"
                    weight="black"
                    uppercase
                    className="text-blue-500"
                  >
                    COERÊNCIA, RACIOCÍNIO CLÍNICO
                  </Typography>{' '}
                  e{' '}
                  <Typography
                    as="strong"
                    weight="black"
                    uppercase
                    className="text-blue-500"
                  >
                    INDIVIDUALIZAÇÃO
                  </Typography>{' '}
                  dos atendimentos.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

const Description = ({paragraph01, paragraph02, paragraph03}) => {
  return (
    <div className="lg:overflow-hidden">
      <div className="h-20 bg-green-100-to-500-x">
        <div className="flex items-center justify-between h-20 mx-auto max-w-screen-lg">
          <Typography
            variant="heading-4xl"
            className="w-full px-10 text-center lg:text-left lg:px-auto"
            uppercase
            weight="black"
          >
            O Evento
          </Typography>
        </div>
      </div>
      <section className="mx-auto -mt-20 max-w-screen-lg">
        <div className="grid gap-2.5 grid-cols-4 grid-flow-col mt-28 lg:grid-cols-12 lg:gap-4 lg:h-100">
          <div className="z-10 col-start-1 col-end-5 lg:col-start-1 lg:col-end-6">
            <Typography
              as="p"
              size="lg"
              className="px-10 mb-4 leading-8 lg:px-auto"
              weight="light"
              align="text-center lg:text-left"
            >
              {paragraph01}
            </Typography>
          </div>
          <div className="relative overflow-hidden lg:overflow-visible col-start-1 col-end-5 lg:col-start-6 lg:col-end-13">
            <div className="-ml-48 w-700 lg:absolute lg:ml-1 lg:-top-44 lg:-right-40">
              <img src={imageEad} alt="" />
            </div>
          </div>
          <div className="z-10 col-start-1 col-end-5 lg:col-start-1 lg:col-end-6">
            <Typography
              as="p"
              className="px-10 leading-8 lg:px-auto"
              weight="light"
              align="text-center lg:text-left"
            >
              {paragraph02}
            </Typography>
            <br />
            <Typography
              as="p"
              className="px-10 leading-8 lg:px-auto"
              weight="light"
              align="text-center lg:text-left"
            >
              {paragraph03}
            </Typography>
          </div>
        </div>
      </section>
    </div>
  )
}

const Professor = ({photo, index, size}) => {
  const styles = {
    img: isFull =>
      clsx(
        isFull ? 'w-full h-full' : 'w-22 h-22',
        'object-cover object-top rounded-full',
      ),
  }

  if (size >= 5) {
    if (index === 0) {
      return <img src={photo} alt="" className={styles.img(true)} />
    }
    return null
  }

  if (index === 0) {
    if (size === 3) {
      return (
        <img
          src={photo}
          alt=""
          className={clsx(
            styles.img(),
            'z-10 absolute left-1/2 top-0 transform -translate-x-1/2',
          )}
        />
      )
    }
    return (
      <img
        src={photo}
        alt=""
        className={clsx(styles.img(), 'z-10 absolute left-0 top-0')}
      />
    )
  }

  if (index === 1) {
    return (
      <img
        src={photo}
        alt=""
        className={clsx(styles.img(), 'z-30 absolute right-0 bottom-0')}
      />
    )
  }

  if (index === 2) {
    return (
      <img
        src={photo}
        alt=""
        className={clsx(styles.img(), 'z-10 absolute left-0 bottom-0')}
      />
    )
  }
  if (index === 3) {
    return (
      <img
        src={photo}
        alt=""
        className={clsx(styles.img(), 'z-10 absolute right-0 top-0')}
      />
    )
  }

  return <img src={photo} alt="" className={styles.img(true)} />
}

const Professors = ({list = []}) => {
  const SIZE = list.length
  const [firstProfessor] = list

  if (SIZE === 1) {
    return (
      <div className="relative w-40 h-40">
        <Professor {...firstProfessor} />
      </div>
    )
  }

  return (
    <div className="relative w-40 h-40">
      {list.map((professor, index) => (
        <Professor
          key={`professor-list-${index}`}
          {...professor}
          size={SIZE}
          index={index}
        />
      ))}
    </div>
  )
}

const CourseDetail = ({
  courseName,
  startTime,
  endTime,
  description,
  professors,
  last,
  isEven,
}) => {
  const allProfessors = useProfessorsQuery()

  const professorsObj = professors.map(name => {
    return allProfessors.find(p => p.name === name)
  })

  const styles = clsx(
    `px-4 flex flex-col py-10 border-gray-100 col-start-1 col-end-5 lg:col-start-1 lg:col-end-13`,
    !last && `border-b-2`,
    isEven ? 'lg:flex-row-reverse' : 'lg:flex-row',
  )

  return (
    <div className={styles}>
      <div className="flex self-center flex-shrink">
        <Professors list={professorsObj} />
      </div>
      <div className="px-4">
        <Typography
          size="lg"
          weight="black"
          className={clsx(
            'text-blue-500 text-center mb-3',
            isEven ? 'lg:text-right' : 'lg:text-left',
          )}
        >
          {dayjs(startTime).locale('pt-br').format('HH[h]mm')} às{' '}
          {dayjs(endTime).locale('pt-br').format('HH[h]mm')}
        </Typography>
        <Typography
          className={clsx(
            'text-center',
            isEven ? 'lg:text-right' : 'lg:text-left',
          )}
        >
          <Typography as="span" weight="bold" uppercase>
            {courseName}
          </Typography>
          <Typography as="span"> | </Typography>
          {professors.map((name, index) => (
            <>
              <Typography
                as="span"
                weight="black"
                className="text-blue-500"
                uppercase
              >
                {name}
              </Typography>

              {professors.length - 1 !== index && (
                <Typography as="span" weight="black" uppercase>
                  {professors.length - 2 === index ? ' e ' : ', '}
                </Typography>
              )}
            </>
          ))}
        </Typography>
        <Typography
          className={clsx(
            'text-center',
            isEven ? 'lg:text-right' : 'lg:text-left',
          )}
        >
          {description}
        </Typography>
      </div>
    </div>
  )
}

const ScheduleDay = ({date, scheduleDay, active}) => {
  return (
    active && (
      <>
        <div className="mt-12 bg-blue-500 shadow-3 col-start-1 col-end-5 lg:col-start-1 lg:col-end-13">
          <Typography
            as="p"
            variant="heading-3xl"
            className="p-4 text-center text-white"
            uppercase
            weight="black"
          >
            {dayjs(date).locale('pt-br').format('DD [de] MMMM')}
          </Typography>
        </div>

        {scheduleDay.map((course, index) => (
          <CourseDetail
            {...course}
            last={scheduleDay.length - 1 === index}
            isEven={index % 2 !== 0}
          />
        ))}
      </>
    )
  )
}

export const ScheduleEvent = ({button, schedule}) => {
  const [activeDay, setActiveDay] = React.useState(0)

  const handleActive = index => {
    setActiveDay(index)
  }

  return (
    <div className="relative mt-10">
      <div className="relative z-10 bg-gray-75 shadow-3">
        <div className="flex items-center justify-center mx-auto max-w-screen-lg">
          <Typography
            variant="heading-4xl"
            className="py-3 text-blue-500"
            weight="black"
            align="center"
            uppercase
          >
            agenda do evento
          </Typography>
        </div>
      </div>

      <ButtonDaysCarousel
        list={schedule.map(({date}) => date)}
        onActive={handleActive}
      />

      <div className="relative mx-auto max-w-screen-lg">
        <div className="z-10 relative grid gap-2.5 grid-cols-4 grid-flow-col lg:grid-cols-12 lg:gap-4">
          <div className="relative px-10 col-start-1 col-end-5 lg:col-start-3 lg:col-end-11 lg:px-0">
            <div className="shadow-lg">
              <div className="relative grid gap-2.5 grid-cols-4 grid-flow-col lg:grid-cols-12 lg:gap-4 bg-white">
                {schedule.map((item, index) => (
                  <ScheduleDay
                    key={`scheduleday-item-${index}`}
                    {...item}
                    active={activeDay === index}
                  />
                ))}
              </div>
            </div>
            <div className="absolute z-10 px-4 -mt-4 left-1/2 transform -translate-x-2/4 col-start-1 col-end-5 lg:col-start-4 lg:col-end-11">
              <Button className="w-full uppercase" as="a" href={button.link}>
                {button.label}
              </Button>
            </div>
          </div>
        </div>
        <div
          className="absolute left-0 w-56 bg-right bg-no-repeat -z-10 top-1/2 lg:-left-12 h-39 transform -translate-y-1/4 lg:-translate-y-1/2 rotate-180"
          style={{backgroundImage: `url(${waveRight})`}}
        ></div>
        <div
          className="absolute right-0 w-56 bg-right bg-no-repeat -z-10 top-1/2 lg:-right-12 h-39 transform -translate-y-3/4 lg:-translate-y-1/2"
          style={{backgroundImage: `url(${waveRight})`}}
        ></div>
      </div>
    </div>
  )
}

const Newsletter = () => {
  const footer = useFooterContext()
  return (
    <div className="my-10 bg-gradient-to-r from-blue-500 to-blue-900 shadow-1">
      <div className="grid gap-2.5 grid-cols-4 lg:grid-cols-12 lg:gap-4 mx-auto max-w-screen-lg py-5 relative">
        <Typography
          variant="heading2"
          className="self-center h-20 text-white col-start-1 col-end-5 lg:col-start-2 lg:col-end-8 lg:h-auto"
          align="text-center lg:text-left"
          uppercase
        >
          Receba notificações dos próximos eventos:
        </Typography>
        <Button
          className="absolute bottom-0 left-0 uppercase transform -translate-x-2/4 translate-y-2/4 lg:relative lg:-translate-x-0 lg:translate-y-0 col-start-3 col-end-5 lg:col-start-8 lg:col-end-12"
          onClick={footer.focus}
        >
          cadastre-se na nossa news!
        </Button>
      </div>
    </div>
  )
}

const TemplatePage = ({
  eventInfo,
  heroBannerSingle,
  descriptionEvent,
  button,
  schedule,
  patreonList,
  ...props
}) => {
  return (
    <div>
      <Hero {...heroBannerSingle} />
      <div className="shadow-down">
        <Chamada eventInfo={eventInfo} />
        <Description {...descriptionEvent} />
        <ScheduleEvent button={button} schedule={schedule} />
        <Patreon list={patreonList} />
        <Newsletter />
      </div>
    </div>
  )
}

const Template = ({pageContext}) => (
  <TemplatePage {...pageContext?.frontmatter} />
)

export default Template
